export const setStyleProperties = <
  E extends HTMLElement,
  P extends { [propName: string]: any },
>(
  $el: E,
  props: P,
  propValueConverter?: <K extends keyof P>(
    prop: Exclude<K, symbol>,
    value: P[K],
  ) => any,
) => {
  if (!($el instanceof HTMLElement) || Object.hasOwn($el, 'style')) return;

  Object.entries(props).forEach(([prop, value]) => {
    const _value = propValueConverter
      ? (propValueConverter(prop, value) ?? null)
      : (value ?? null);

    if (Object.hasOwn($el.style, prop)) {
      $el.style.setProperty(prop, _value);
    } else {
      $el.style.setProperty(`--${prop.replace(/^-+/gi, '')}`, _value);
    }
  });
};

export const getScrollableAncestorList = (element: HTMLElement) => {
  const list: (HTMLElement | Window)[] = [];
  let current = element.parentElement;

  while (current) {
    const { overflow, overflowX, overflowY } = getComputedStyle(current);

    if (/auto|scroll|overlay/.test(overflow + overflowX + overflowY)) {
      list.push(current);
    }

    current = current.parentElement;
  }

  list.push(window);

  return list;
};

export const getAncestorList = (element: HTMLElement) => {
  const list: HTMLElement[] = [];
  let current = element.parentElement;

  while (current && current !== document.body) {
    list.push(current);

    current = current.parentElement;
  }

  return list;
};

export type ElementOrRef =
  | Element
  | Document
  | null
  | React.RefObject<Element | Document | null>;

export const resolveElementOrRef = (
  value: ElementOrRef,
): Element | Document | null => {
  const ref = value as React.RefObject<Element | Document | null>;
  return value && typeof ref.current !== 'undefined'
    ? (ref.current ?? null)
    : (value as Element | Document | null);
};
